// Getting a song ready to play and to analyse, holding as little as possible while it is done.
//
// What is held, for a five-minute stereo file at 48 kHz (D = 115.2 MB decoded, half of it for the mono mix, B = the encoded file):
//   - the encoded bytes are hashed in place (no second copy) and then handed to the decoder, which takes them;
//   - the decoded AudioBuffer (D) is read once for the mono mix (D/2), and its channel arrays are then moved out of it
//     (transferred where the browser allows it, copied where it does not), and the AudioBuffer is let go of;
//   - the channels go to the stretch worklet and the mono mix to the analysis worker by transfer, so nothing is left here.
// A `Ledger` counts these arrays as they are taken and let go, so the figures can be read and tested, not guessed.

/** Longer than this is refused before anything is copied: it would need more memory than a phone has. */
export const MAX_SONG_SECONDS = 15 * 60;

export class TooLong extends Error {
  constructor(readonly seconds: number) {
    super('song too long');
  }
}

/** Bytes of arrays held, by name. `peak` is the most held at any moment since it was made or last reset. */
export class Ledger {
  private readonly held = new Map<string, number>();
  peak = 0;

  get bytes(): number {
    let n = 0;
    for (const b of this.held.values()) n += b;
    return n;
  }

  add(name: string, bytes: number): void {
    this.held.set(name, (this.held.get(name) ?? 0) + bytes);
    this.peak = Math.max(this.peak, this.bytes);
  }

  release(name: string): void {
    this.held.delete(name);
  }

  /** What is held now, by name. */
  get items(): Record<string, number> {
    return Object.fromEntries(this.held);
  }
}

/** The part of an AudioBuffer this uses. */
export interface DecodedLike {
  readonly numberOfChannels: number;
  readonly length: number;
  readonly sampleRate: number;
  readonly duration: number;
  getChannelData(channel: number): Float32Array;
}

export interface Prepared {
  readonly id: string;
  readonly duration: number;
  readonly sampleRate: number;
  /** One array per channel: owned by the caller, to be moved to the stretch worklet. */
  readonly channels: Float32Array[];
  /** The channels averaged: owned by the caller, to be moved to the analysis worker. */
  readonly mono: Float32Array;
}

export interface Io {
  hash(bytes: ArrayBuffer): Promise<string>;
  /** Takes the bytes (a decoder detaches them). */
  decode(bytes: ArrayBuffer): Promise<DecodedLike>;
}

/** The channel's own buffer, moved out of the decoder's array when the browser lets us, else a copy. */
function takeChannel(data: Float32Array): { array: Float32Array; moved: boolean } {
  const { byteOffset, length } = data; // read before the transfer empties the array
  try {
    const moved = structuredClone(data.buffer, { transfer: [data.buffer] }) as ArrayBuffer;
    return { array: new Float32Array(moved, byteOffset, length), moved: true };
  } catch {
    return { array: data.slice(), moved: false };
  }
}

export async function prepareSong(bytes: ArrayBuffer, io: Io, ledger: Ledger, maxSeconds = MAX_SONG_SECONDS): Promise<Prepared> {
  ledger.add('encoded', bytes.byteLength);
  let id: string;
  let decoded: DecodedLike | null;
  try {
    id = await io.hash(bytes); // in place: the bytes are not copied to be hashed
    decoded = await io.decode(bytes);
  } finally {
    ledger.release('encoded'); // the decoder took the bytes (or the attempt failed)
  }
  const n = decoded.length;
  const count = decoded.numberOfChannels;
  const { duration, sampleRate } = decoded;
  ledger.add('decoded', n * count * 4);
  if (duration > maxSeconds) {
    ledger.release('decoded');
    throw new TooLong(duration);
  }
  const mono = new Float32Array(n);
  ledger.add('mono', n * 4);
  for (let c = 0; c < count; c++) {
    const ch = decoded.getChannelData(c);
    for (let i = 0; i < n; i++) mono[i]! += ch[i]! / count;
  }
  const channels: Float32Array[] = [];
  let copied = false;
  for (let c = 0; c < count; c++) {
    const taken = takeChannel(decoded.getChannelData(c));
    channels.push(taken.array);
    copied ||= !taken.moved;
  }
  decoded = null; // the AudioBuffer is not kept: its samples are the channels now
  if (!copied) ledger.release('decoded'); // moved, not copied: the same memory, now owned here
  ledger.add('channels', n * count * 4);
  ledger.release('decoded'); // (where it had to be copied, the copy was the peak)
  return { id, duration, sampleRate, channels, mono };
}
